import { useMutation } from "@tanstack/react-query";
import { getAuthHeaders, getApiBaseUrl } from "@/lib/api";

export interface FurstSearchResult {
  proveId: string;
  proveTattDato: string;
  proveStatus: string;
  patientName?: string;
  isAminoUser: boolean;
  authUserId?: string;
  existingLabVisit?: { id: string; status: string };
}

export interface FurstMappedBiometric {
  biometricId: string;
  biometricName: string;
  analyseNavn: string;
  value: string;
  unit: string;
  arithmetic: string;
  matchMethod: "nlk" | "furstId" | "name";
}

export interface FurstSkippedAnalysis {
  analyseNavn: string;
  svar: string;
  reason: "incomplete" | "non_numeric" | "unmapped";
}

export interface FurstImportResult {
  proveId: string;
  success: boolean;
  dryRun: boolean;
  labVisitId?: string;
  labVisitCreated?: boolean;
  existingLabVisitStatus?: string;
  labVisitStatus?: string;
  resultsImported?: number;
  resultsSkipped?: number;
  unmappedAnalyses?: string[];
  mappedBiometrics?: FurstMappedBiometric[];
  skippedAnalyses?: FurstSkippedAnalysis[];
  error?: string;
}

export const useFurstImportSearch = () =>
  useMutation({
    mutationFn: async (opts: {
      furstCookie: string;
      fromDate: string;
      toDate: string;
    }) => {
      const res = await fetch(
        `${getApiBaseUrl()}/api/admin/furst-import/search`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeaders(),
          },
          body: JSON.stringify(opts),
        }
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || `Search failed (${res.status})`);
      }
      const data = await res.json();
      return data.results as FurstSearchResult[];
    },
  });

export const useFurstImportExecute = () =>
  useMutation({
    mutationFn: async (opts: {
      furstCookie: string;
      dryRun: boolean;
      requisitions: Array<{
        proveId: string;
        authUserId: string;
        proveTattDato: string;
        proveStatus: string;
      }>;
    }) => {
      const res = await fetch(
        `${getApiBaseUrl()}/api/admin/furst-import/import`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeaders(),
          },
          body: JSON.stringify(opts),
        }
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || `Import failed (${res.status})`);
      }
      const data = await res.json();
      return data.results as FurstImportResult[];
    },
  });
